import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AdminFormFieldDropdown,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  BulkAPIs,
  BulkApiTags,
  bulkUploadData,
  useCreateBulkUploadRequestMutation,
  useGetBulkProcessStatusQuery,
  useRunBulkUploadProcessMutation,
} from "../../../api/network/bulkUploadApiServices";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  CreateBulkUploadRequestResponse,
  RunBulkProcessResponse,
} from "../../../api/types/BulkUpload";
import { SelectFileInput } from "../../../components/admin/selectFile";
import { UPLOAD_TYPE_OBJ } from "./constants";
import { BulkStatusModal } from "../../../components/statusModals";
import {
  APP_CONFIG,
  MODAL_TYPE,
  SCHEDULER_PROCESS_STATUS,
} from "../../../constants/constants";
import { useDispatch } from "react-redux";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";

const ImportScreen: React.FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.import_export",
  });
  const { t: tImport } = useTranslation("translation", {
    keyPrefix: "admins.import_export.import",
  });
  const { t: tToast } = useTranslation("translation", {
    keyPrefix: "admins.import_export.toast",
  });
  const { getUserACLAccessLevel } = useACL();
  const thisOrgZipTieAllowed = useLoggedInUserData("serializedziptieallowed");
  const dispatch = useDispatch();
  const [selectedCategory, setSelectedCategory] = useState<number | null>();

  const [createUploadRequestTrigger] = useCreateBulkUploadRequestMutation();
  const [runBulkUploadProcessTrigger] = useRunBulkUploadProcessMutation();

  const [processData, setProcessData] = useState<RunBulkProcessResponse | null>(
    null
  );
  const [intervalCount, setIntervalCount] = useState(0);
  const intervalRef = useRef<any>(0);
  const [modalType, setModalType] = useState<MODAL_TYPE>(null);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (modalType === "upload") {
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const handleGoBack = (event: PopStateEvent) => {
      if (modalType === "upload") {
        window.history.forward();
      }
    };
    window.addEventListener("popstate", handleGoBack);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handleGoBack);
    };
  }, [modalType]);

  console.log("processData", processData?.id);

  const { data: processStatusData, currentData } = useGetBulkProcessStatusQuery(
    processData?.id ?? 0,
    {
      skip: !processData?.id,
      pollingInterval: processData
        ? APP_CONFIG.API_POLLING_INTERVALS.UPLOADS_DOWNLOADS
        : 0,
      refetchOnReconnect: true,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skipPollingIfUnfocused: true,
    }
  );

  const invalidateProcessStatusData = () => {
    dispatch(BulkAPIs.util.invalidateTags([BulkApiTags.RESET_STATUS]));
  };

  const clearProcessingData = () => {
    setProcessData(null);
    invalidateProcessStatusData();
    window.clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIntervalCount(0);
  };

  useEffect(() => {
    if (!processData) {
      return;
    }
    if (
      processStatusData?.status === SCHEDULER_PROCESS_STATUS.COMPLETED &&
      intervalCount > 0
    ) {
      clearProcessingData();
      setModalType("success");
    } else if (
      processStatusData?.status === SCHEDULER_PROCESS_STATUS.FAILED &&
      intervalCount > 0
    ) {
      clearProcessingData();
      setModalType("error");
    } else if (
      processStatusData?.status === SCHEDULER_PROCESS_STATUS.RUNNING &&
      intervalCount === 30
    ) {
      clearProcessingData();
      setModalType("longProcess");
    }
  }, [processStatusData, intervalCount, processData]);

  const handleCategoryChange = (option: TSelectboxOption | null) => {
    if (option) setSelectedCategory(parseInt(option.value));
  };

  const handleImportData = (file: File) => {
    // Logic to export data for the selected category
    createUploadRequestTrigger({ name: file.name })
      .unwrap()
      .then((data: CreateBulkUploadRequestResponse) => {
        if (!!data?.id) {
          handleBulkUpload(data, file);
        } else {
          setModalType("error");
        }
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.create_failed")];
        toast.error(errors?.join(" "));
      });
  };

  const handleBulkUpload = (
    data: CreateBulkUploadRequestResponse,
    file: File
  ) => {
    const abortController = new AbortController();
    setAbortController(abortController);
    setModalType("upload");
    bulkUploadData(
      { s3Url: data.upload_url, data: file },
      abortController.signal,
      (response: any) => {
        if (!!response) {
          // File has been uploaded successfully to s3 server. Now, run the bulk prcess at own server
          runProcess(1, {
            uploaded_file: data.id,
            upload_type: selectedCategory,
          });
        }
      },
      (error: any) => {
        setModalType("error");
      }
    );
  };

  const runProcess = (process: number, param: any) => {
    invalidateProcessStatusData();
    setModalType("processing");
    runBulkUploadProcessTrigger({ process, param })
      .unwrap()
      .then((data) => {
        if (!!data?.id) {
          // Process has been started successfully
          setProcessData(data);
          startLongProcessInterval();
        } else {
          setModalType("error");
        }
      })
      .catch((error) => {
        console.log("Processing error", error);
        setModalType("error");
      });
  };

  const startLongProcessInterval = () => {
    if (!intervalRef) {
      return;
    }
    intervalRef.current = setInterval(() => {
      // incement the progress
      setIntervalCount((count) => count + 1);
    }, 1000);
  };

  const getModalHeading: string | null = useMemo(() => {
    switch (modalType) {
      case "upload":
      case "processing":
      case "longProcess":
        return tToast("processing");
      case "success":
        return tToast("success");
      case "error":
        return tToast("error");
      default:
        return "";
    }
  }, [modalType, tToast]);

  const onModalActionClick = () => {
    if (modalType === "upload" && abortController) {
      abortController.abort();
    }
    clearProcessingData();
    setModalType(null);
  };
    const includeSerializedZipTie = thisOrgZipTieAllowed === "1" && getUserACLAccessLevel(ACL_NAMES.ACL_ZIPTIE_CONFIG) >= ROLE_ACCESS_LEVELS.ALL
    const options = (includeSerializedZipTie
      ? UPLOAD_TYPE_OBJ
      : UPLOAD_TYPE_OBJ.filter((item) => item.value !== 8)
    ).map((item) => ({
      label: item.label,
      value: item.value.toString(),
    }));

  return (
    <div>
      <p className="text-lg font-bold leading-6">{tImport("heading")}</p>
      <AdminFormFieldDropdown
        id="select_admin"
        label={t("selection_heading")}
        name={t("selection_heading")}
        placeholder={t("selection_placeholder")}
        value={selectedCategory ? selectedCategory?.toString() : undefined}
        onChange={handleCategoryChange}
        customWrapperClass="my-4"
        options={options}
      />
      <div className="flex row:between py-4">
        <div className="flex-auto" />
        <>
          <SelectFileInput
            label={
              <span
                className={`flex justify-center whitespace-nowrap ${!selectedCategory ? "text-gray-400" : "text-accent-blue-dark"}`}
              >
                {tImport("import")}
                <img
                  className="ml-2"
                  src={svgIcons.Download}
                  alt={tImport("import")}
                />
              </span>
            }
            id="File"
            onChange={(file) => {
              if (file) {
                setFileName(file.name);
                handleImportData(file);
              }
            }}
            customWrapperClass="mx-2"
            allowedFields={["csv"]}
            disabled={!selectedCategory}
          />
        </>
      </div>
      {modalType !== null && (
        <BulkStatusModal
          heading={getModalHeading}
          subHeading={
            modalType === "longProcess"
              ? tToast("longProcess")
              : modalType === "error"
                ? tToast("error_details")
                : undefined
          }
          type={modalType}
          fileName={fileName ? fileName : ""}
          onHide={onModalActionClick}
        />
      )}
    </div>
  );
};

export default ImportScreen;
